import React from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { TrendingUp, TrendingDown, Wallet, ChevronRight } from 'lucide-react';

const SummaryCards = ({ onForecastClick }) => {
  const { financeData } = useFinance();

  // Función helper para determinar si un pago está activo en el mes actual
  const isPaymentActive = (payment) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    // Si usa fechas exactas
    if (payment.useExactDates && payment.startDate && payment.endDate) {
      const startDate = new Date(payment.startDate);
      const endDate = new Date(payment.endDate);

      // Para pagos fijos, verificar que estemos en el rango
      if (payment.installments === 0) {
        return now >= startDate && now <= endDate;
      }

      // Para pagos a plazos, verificar el rango de fechas
      return now >= startDate && now <= endDate;
    }

    // Sistema antiguo: usar startMonth + installments
    const paymentStartMonth = payment.startMonth || currentMonth;

    // Si el pago es fijo (installments = 0), siempre está activo
    if (payment.installments === 0) {
      return true;
    }

    // Si es un pago a plazos, verificar si está en el rango activo
    if (payment.installments > 0) {
      // Calcular meses desde el inicio considerando cambios de año
      const startYear = new Date().getFullYear(); // Asumimos que empezó este año
      const monthsSinceStart = (now.getFullYear() - startYear) * 12 + (currentMonth - paymentStartMonth);

      // Calcular cuotas restantes
      const remainingInstallments = payment.installments - monthsSinceStart;
      return monthsSinceStart >= 0 && remainingInstallments > 0;
    }

    return false;
  };

  // Calcular total de pagos mensuales activos
  const monthlyPaymentsTotal = financeData.payments.reduce((sum, p) => {
    return isPaymentActive(p) ? sum + p.amount : sum;
  }, 0);

  const totalFixedExpenses = (financeData.rent || 0) + monthlyPaymentsTotal;

  // Calcular pagos pendientes (no pagados) que estén activos
  const unpaidRent = financeData.rentPaid ? 0 : (financeData.rent || 0);
  const unpaidPayments = financeData.payments
    .filter(p => !p.paid && isPaymentActive(p))
    .reduce((sum, p) => sum + p.amount, 0);

  // Balance actual (dinero disponible después de pagar pendientes)
  const finalBalance = (financeData.currentMoney || 0) - unpaidRent - unpaidPayments;

  // Proyección: dinero que tendrás al final del mes
  const projectedBalance = (financeData.currentMoney || 0) + (financeData.salary || 0) - totalFixedExpenses;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
      {/* Dinero en Cuenta */}
      <div className="summary-card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <div className="flex items-center justify-between">
          <h3 className="summary-card-label text-blue-800">💰 Dinero Actual</h3>
          <Wallet className="w-7 h-7 md:w-8 md:h-8 text-blue-600" />
        </div>
        <div className="summary-card-value text-blue-700">
          {formatCurrency(financeData.currentMoney || 0)}
        </div>
        <p className="text-xs md:text-sm text-blue-600 leading-relaxed">
          {financeData.salary > 0
            ? `Proyección: ${formatCurrency(projectedBalance)} tras cobrar y pagar gastos.`
            : 'Introduce tu salario para ver la proyección.'
          }
        </p>
      </div>

      {/* Gastos Mensuales - Clickable */}
      <button
        onClick={onForecastClick}
        className="summary-card bg-gradient-to-br from-red-50 to-red-100 border-red-200 
                   text-left hover:shadow-xl hover:-translate-y-1 cursor-pointer group"
      >
        <div className="flex items-center justify-between">
          <h3 className="summary-card-label text-red-800">💸 Gastos Mensuales</h3>
          <TrendingDown className="w-7 h-7 md:w-8 md:h-8 text-red-600" />
        </div>
        <div className="summary-card-value text-red-700">
          {formatCurrency(monthlyPaymentsTotal)}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs md:text-sm text-red-600">
            Sin alquiler incluido
          </p>
          <div className="flex items-center gap-1 text-red-700 group-hover:translate-x-1 transition-transform">
            <span className="text-xs font-semibold">Ver previsión</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </button>

      {/* Balance Final */}
      <div className="summary-card bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
        <div className="flex items-center justify-between">
          <h3 className="summary-card-label text-indigo-800">⚖️ Disponible Ahora</h3>
          <TrendingUp className="w-7 h-7 md:w-8 md:h-8 text-indigo-600" />
        </div>
        <div className={`summary-card-value ${finalBalance >= 0 ? 'text-indigo-700' : 'text-red-600'}`}>
          {formatCurrency(finalBalance)}
        </div>
        <p className="text-xs md:text-sm text-indigo-600">
          {unpaidPayments > 0 || unpaidRent > 0
            ? `Pendiente: ${formatCurrency(unpaidRent + unpaidPayments)}`
            : '✅ Sin gastos pendientes'
          }
        </p>
      </div>
    </div>
  );
};

export default SummaryCards;